/** Single place that knows how to talk to the Worker. */

export const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

const TOKEN_KEY = 'snipwork-clients.token';

export const storeToken = (token: string) => localStorage.setItem(TOKEN_KEY, token);
export const readToken = () => localStorage.getItem(TOKEN_KEY);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export class ApiError extends Error {
  /**
   * Per-field problems, keyed by the field they concern. The API sends these
   * on a 422 and they were being dropped here, so a caller could only ever
   * show "that configuration has problems" without saying which part.
   */
  constructor(
    public status: number,
    message: string,
    public details?: Record<string, string>,
  ) {
    super(message);
  }
}

/** Set by the auth provider so a 401 anywhere logs the user out once. */
let onExpiry: (() => void) | null = null;
export const setExpiryCallback = (fn: () => void) => { onExpiry = fn; };

export async function requestApi<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = readToken();
  const headers = new Headers(options.headers);
  // A FormData body carries its own multipart boundary in the Content-Type;
  // overriding it with JSON makes the upload unreadable on arrival.
  if (!(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...options, headers });
  } catch {
    throw new ApiError(0, 'No connection to the server. Check your Internet and try again.');
  }

  if (response.status === 401) {
    clearToken();
    onExpiry?.();
    throw new ApiError(401, 'Session expired. Please sign in again.');
  }

  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const failure = body as { error?: string; details?: Record<string, string> };
    throw new ApiError(
      response.status, failure.error ?? 'Something went wrong.', failure.details);
  }
  return body as T;
}

export const get = <T>(path: string) => requestApi<T>(path);
export const post = <T>(path: string, data: unknown) =>
  requestApi<T>(path, { method: 'POST', body: JSON.stringify(data) });
export const put = <T>(path: string, data: unknown) =>
  requestApi<T>(path, { method: 'PUT', body: JSON.stringify(data) });
export const del = <T>(path: string) => requestApi<T>(path, { method: 'DELETE' });
/** A file upload: the form goes as multipart, not through JSON.stringify. */
export const postForm = <T>(path: string, form: FormData) =>
  requestApi<T>(path, { method: 'POST', body: form });

/**
 * Fetch a protected binary (a moderation photograph) as an object URL. An
 * <img> tag cannot carry the bearer token, so the image is fetched here and
 * handed to the browser as a blob. Callers must revoke the URL when done.
 */
export async function fetchBlobUrl(
  path: string, failure = 'Could not load the photograph.',
): Promise<string> {
  const token = readToken();
  const response = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new ApiError(response.status, failure);
  return URL.createObjectURL(await response.blob());
}

/**
 * Fetch a protected document as text, for putting into an iframe's `srcdoc`.
 *
 * The blob-URL trick above cannot be used for a frame: the portal's CSP has no
 * `frame-src`, so it falls back to `default-src 'self'` and a blob: frame is
 * refused. `img-src` was given `blob:` for AuthImage; a frame source was never
 * added, and must not be - see the PreviewModal comment for why srcdoc is the
 * better answer rather than the workaround.
 */
export async function fetchText(
  path: string, failure = 'Could not load that document.',
): Promise<string> {
  const token = readToken();
  const response = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new ApiError(response.status, failure);
  return response.text();
}

/** Turn a filter object into a query string, dropping empty values. */
export function toQuery(filters: Record<string, string | number | undefined | null>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}
